import "./Forms.scss";

import React, { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";

import { CopyToClipboardInput } from "../../../../components/CustomInput/CopyToClipboardInput";
import { CustomInput } from "../../../../components/CustomInput/CustomInput";
import { CustomModal } from "../../../../components/CustomModal";
import type { IOption } from "../../../../components/StyledSelect/StyledSelect";
import { StyledSelect } from "../../../../components/StyledSelect/StyledSelect";
import { useOrganizationContext } from "../../../../contexts/organizations/organizationsContext";
import { useApplications } from "../../../../hooks/reactQueryHooks/useApplications";
import type {
  IGenerateKeyProps,
  IKey,
} from "../../../../hooks/reactQueryHooks/useKeys";
import { useGenerateKey } from "../../../../hooks/reactQueryHooks/useKeys";
import { useCheckAccess } from "../../../../hooks/useCheckAccess";

interface IProps {
  onCancel: () => void;
}

type KeyType = "proxy" | "management";

const GenerateApiKeyForm: React.FC<IProps> = ({ onCancel }) => {
  const { currentOrganization } = useOrganizationContext();
  const [generated, setGenerated] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [selectedApplication, setSelectedApplication] =
    useState<IOption | null>(null);
  const [keyType, setKeyType] = useState<KeyType>("proxy");

  const noSubscriptionError = `Generating API keys is disabled while there is no active subscription for the ${currentOrganization?.name} organization.`;

  const generateKeyMutation = useGenerateKey();
  const { displayError } = useCheckAccess(
    noSubscriptionError,
    generateKeyMutation.isError,
  );

  const handleGenerateKey = async (
    keyRequest: IGenerateKeyProps,
  ): Promise<IKey> => {
    return await generateKeyMutation.mutateAsync(keyRequest);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSubmit = async () => {
    const organizationId = currentOrganization?.id || "";
    if (
      organizationId &&
      inputValue &&
      (keyType === "management" ||
        (keyType === "proxy" && selectedApplication?.value))
    ) {
      const fetchedKeyData: IKey = await handleGenerateKey({
        organizationId,
        name: inputValue,
        applicationId: selectedApplication?.value,
        apiKeyType: keyType,
      });

      const { key } = fetchedKeyData;
      if (key) {
        setGenerated(key);
      } else {
        onCancel();
      }
    }
  };

  const { data: applications = [] } = useApplications(
    currentOrganization?.id || "",
  );

  const handleCancel = () => {
    generateKeyMutation.reset();
    onCancel();
  };

  const handleSelectApplication = (option: IOption) => {
    setSelectedApplication(option);
  };

  const footerButtons = (isProcessing: boolean) => {
    return generated ? (
      <Button
        className="popup-footer__button-secondary btn btn-secondary btn-sm"
        size="sm"
        variant="secondary"
        onClick={handleCancel}
      >
        Close
      </Button>
    ) : (
      <>
        <Button size="sm" variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          className="btn btn-primary"
          disabled={
            !inputValue.trim() ||
            (keyType === "proxy" && !selectedApplication) ||
            isProcessing ||
            !!displayError
          }
          type="submit"
          onClick={handleSubmit}
        >
          Generate
          {isProcessing && (
            <Spinner
              role="status"
              className="ms-2"
              size="sm"
              animation="border"
            />
          )}
        </Button>
      </>
    );
  };

  return (
    <CustomModal
      show={true}
      handleClose={onCancel}
      title={generated ? "API key generated" : "Generate API key"}
      footer={footerButtons(generateKeyMutation.isLoading)}
      errorMessage={displayError}
    >
      <div>
        {!generated ? (
          <>
            <p className="mb-7">
              The CogCache API key will be used to authenticate and authorise
              all the CogCache API requests.
            </p>
            <CustomInput
              label="API key name"
              handleChange={handleChange}
              inputValue={inputValue}
              placeholder="Enter the API key name"
              maxLength={150}
            />
            <label className="d-block mt-7 fs-7 required form-label">
              API key type
            </label>
            <div className="d-flex flex-row mb-7">
              <div className="form-check-radio d-flex flex-row align-items-center">
                <input
                  type="radio"
                  name="apiType"
                  id="apiType-proxy"
                  value="proxy"
                  checked={keyType === "proxy"}
                  onChange={() => setKeyType("proxy")}
                />
                <label
                  htmlFor="apiType-proxy"
                  className="form-check-label px-2"
                >
                  Proxy API
                </label>
              </div>
              <div className="d-flex mx-2 flex-row">
                <div className="form-check-radio d-flex flex-row al align-items-center px-4">
                  <input
                    type="radio"
                    name="apiType"
                    value="management"
                    id="apiType-management"
                    checked={keyType === "management"}
                    onChange={() => setKeyType("management")}
                  />
                  <label
                    htmlFor="apiType-management"
                    className="form-check-label px-2"
                  >
                    Management API
                  </label>
                </div>
              </div>
            </div>
            {keyType === "proxy" && (
              <>
                <label className="d-block mt-7 fs-7 required form-label">
                  Project
                </label>
                <StyledSelect
                  isClearable={true}
                  isDisabled={false}
                  isLoading={false}
                  isSearchable={false}
                  options={applications?.map(app => ({
                    label: app.name,
                    value: app.id,
                  }))}
                  onChange={handleSelectApplication}
                  placeholder="Select a project"
                  value={selectedApplication || undefined}
                />
              </>
            )}
          </>
        ) : (
          <>
            <p>
              Please do not share the CogCache API key and make sure is stored
              somewhere secure. You will not be able to see it again.
            </p>
            <CopyToClipboardInput label="API key" inputValue={generated} />
          </>
        )}
      </div>
    </CustomModal>
  );
};

export { GenerateApiKeyForm };
